import { useEffect, useState } from 'react'
import { LogIn, Loader2 } from 'lucide-react'
import { useApp } from '../../store/AppContext'
import { login as apiLogin, loginWithGoogle, getAuthConfig, setToken, type LoginResponse } from '../../api/auth'
import { initializeFirebase } from '../../firebase/config'

declare global {
    interface Window {
        google?: any
    }
}

export default function LoginPage() {
    const { setCurrentSchool } = useApp()
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [googleClientId, setGoogleClientId] = useState<string | null>(null)

    // Shared post-login flow for both password and Google sign-in
    function completeLogin(response: LoginResponse) {
        setToken(response.token)
        initializeFirebase(response.school.firebaseUrl)
        setCurrentSchool({
            id: response.school.firebaseUrl,
            name: response.school.name,
            username: response.school.username,
            firebaseUrl: response.school.firebaseUrl,
            isAdmin: response.school.isAdmin
        })
    }

    async function handleLogin(e: React.FormEvent) {
        e.preventDefault()
        setError('')
        setLoading(true)

        try {
            const response = await apiLogin(username, password)
            completeLogin(response)
            setLoading(false)
        } catch (err: any) {
            setError(err.message || 'Login failed')
            setLoading(false)
        }
    }

    // Load Google Identity Services and render the button when the server advertises a client id
    useEffect(() => {
        let cancelled = false

        getAuthConfig().then((cfg) => {
            if (cancelled || !cfg.googleClientId) return
            setGoogleClientId(cfg.googleClientId)

            const init = () => {
                if (cancelled || !window.google?.accounts?.id) return
                window.google.accounts.id.initialize({
                    client_id: cfg.googleClientId,
                    callback: async (resp: any) => {
                        setError('')
                        setLoading(true)
                        try {
                            const r = await loginWithGoogle(resp.credential)
                            completeLogin(r)
                        } catch (err: any) {
                            setError(err.message || 'Google login failed')
                        } finally {
                            if (!cancelled) setLoading(false)
                        }
                    }
                })
                const el = document.getElementById('google-signin-btn')
                if (el) {
                    window.google.accounts.id.renderButton(el, {
                        theme: 'filled_blue',
                        size: 'large',
                        shape: 'pill',
                        text: 'signin_with',
                        width: 320
                    })
                }
            }

            if (window.google?.accounts?.id) {
                init()
            } else {
                const existing = document.getElementById('gis-script') as HTMLScriptElement | null
                if (existing) {
                    existing.addEventListener('load', init)
                } else {
                    const s = document.createElement('script')
                    s.src = 'https://accounts.google.com/gsi/client'
                    s.async = true
                    s.defer = true
                    s.id = 'gis-script'
                    s.onload = init
                    document.body.appendChild(s)
                }
            }
        })

        return () => {
            cancelled = true
        }
    }, [])

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
            {/* Animated background */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
                <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="relative z-10 w-full max-w-md">
                {/* Logo/Title */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500 rounded-full mb-4 shadow-lg">
                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z" />
                            <path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z" />
                            <path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">Boat Tracker</h1>
                    <p className="text-primary-400">v1.2 DEBUG</p>
                </div>

                {/* Login Card */}
                <div className="glass-dark rounded-2xl shadow-2xl p-8">
                    <h2 className="text-2xl font-semibold text-white mb-6">School Login</h2>

                    {googleClientId && (
                        <div className="mb-6">
                            <div id="google-signin-btn" className="flex justify-center"></div>
                            <div className="flex items-center gap-3 mt-6">
                                <div className="flex-1 h-px bg-white/10"></div>
                                <span className="text-xs text-white/40 uppercase tracking-wide">or sign in with a password</span>
                                <div className="flex-1 h-px bg-white/10"></div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-white/80 mb-2">
                                Username
                            </label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="input-field bg-white/10 text-white placeholder-white/40 border-white/20"
                                placeholder="school-username"
                                required
                                disabled={loading}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-white/80 mb-2">
                                Password
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input-field bg-white/10 text-white placeholder-white/40 border-white/20"
                                placeholder="••••••••"
                                required
                                disabled={loading}
                            />
                        </div>

                        {error && (
                            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Logging in...
                                </>
                            ) : (
                                <>
                                    <LogIn className="w-5 h-5" />
                                    Login
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-white/10">
                        <p className="text-xs text-white/40 text-center">
                            Contact your administrator for login credentials
                        </p>
                    </div>
                </div>
            </div>

            <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
        </div>
    )
}
